import { EditOutlined, ShopOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Modal, Radio, Row, Select, Typography } from "antd";
import FormInput from "components/FormInput";
import { INPUT_TYPE } from "config/constants";
import { SHIPPING_TYPE } from "features/Cart/constants";
import { t } from "i18next";
import { compact, join } from "lodash";
import { Trans } from "react-i18next";
import PickupWarehousePanel from "../PickupWarehouseSelect/PickupWarehousePanel";
import useModalChangeShippingMethod from "./useModalChangeShippingMethod";

/**
 * Pop up "Thay đổi thông tin phương thức vận chuyển đơn [Mã đơn]".
 * Mở từ icon [Chỉnh sửa] ở khối thông tin giao hàng của màn Chi tiết đơn hàng.
 */
const ModalChangeShippingMethod = ({ visible, onClose, order, onRefetch }) => {
  const {
    form,
    shippingType,
    shippingTypeOptions,
    providerOptions,
    fetchShippingProvider,
    onChangeShippingType,
    selectedPickup,
    setSelectedPickup,
    submitting,
    handleConfirm,
  } = useModalChangeShippingMethod({ order, visible, onClose, onRefetch });

  // Khớp mockup: dấu * bắt buộc nằm SAU tên field thay vì trước (mặc định AntD tự thêm `::before`
  // khi field có rules required) — cùng cách xử lý với label "Phương thức vận chuyển" bên dưới.
  const renderRequiredLabel = (text) => (
    <>
      {text}
      <span style={{ color: "#ff4d4f" }}>&nbsp;*</span>
    </>
  );

  // Nhà xe — 4 ô theo lưới 2 cột, chỉ "Tên nhà xe" bắt buộc, ô nào không nhập để trắng.
  const busConfigFields = (
    <Row gutter={[16, 0]}>
      {[
        { name: "busName", label: t("cart.busNamePlaceholder"), required: true },
        { name: "location", label: t("cart.locationPlaceholder") },
        { name: "telephone", label: t("cart.telephonePlaceholder") },
        { name: "busStation", label: t("cart.busStationPlaceholder") },
      ].map((field) => (
        <Col span={12} key={field.name}>
          <Form.Item
            label={field.required ? renderRequiredLabel(field.label) : field.label}
            name={["shippingConfiguration", "busConfig", field.name]}
            rules={field.required ? [{ required: true, message: t("cart.blankBusName") }] : undefined}
            required={false}
          >
            <Input placeholder={field.label} maxLength={255} />
          </Form.Item>
        </Col>
      ))}
    </Row>
  );

  const providerField = (
    <Form.Item
      name={["shippingConfiguration", "deliver3PartyConfig", "providerID"]}
      rules={[{ required: true, message: t("cart.blankServiceCode") }]}
      style={{ marginBottom: 0 }}
    >
      <Select
        placeholder={t("cart.serviceCodePlaceholder")}
        options={providerOptions}
        showSearch
        optionFilterProp="label"
        // Đã tự tải sẵn ngay khi PTVC đang chọn cần ĐVVC (xem `useModalChangeShippingMethod`) —
        // đây chỉ để làm mới lại data khi user chủ động mở lại dropdown.
        onDropdownVisibleChange={(open) => open && fetchShippingProvider()}
      />
    </Form.Item>
  );

  // Phí giao nội thành — cùng input + quy tắc validate với màn giỏ hàng (`AdditionalDeliver`).
  const urbanCODField = (
    <FormInput
      type={INPUT_TYPE.CURRENCY_INPUT}
      formItemOptions={{
        name: ["shippingConfiguration", "urbanConfig", "fee"],
        rules: [{ required: true, message: t("cart.blankFee") }],
        style: { marginBottom: 0 },
      }}
      inputOptions={{
        placeholder: t("cart.urbanCODFeePlaceholder"),
        style: { width: "100%" },
      }}
    />
  );

  // Tại kho — dòng tóm tắt người nhận + panel chọn kho/người nhận NHÚNG THẲNG trong popup (khớp
  // mockup): chọn phát ăn ngay vào `selectedPickup`, không có bước "Xác nhận" riêng — commit thật sự
  // nằm ở nút [Xác nhận] của popup cha.
  const pickupField = (
    <>
      <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, background: "#fff", marginBottom: 12 }}>
        <Row align="middle" wrap={false} style={{ padding: "12px 16px" }}>
          <Col style={{ width: 130, flexShrink: 0 }}>
            <Row align="middle" gutter={6} wrap={false}>
              <Col>
                <ShopOutlined style={{ color: "#6B7280", fontSize: 13 }} />
              </Col>
              <Col>
                <Typography.Text style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>
                  {t("v2.order.detail.modalChangeShippingMethod.pickupLabel")}
                </Typography.Text>
              </Col>
            </Row>
          </Col>
          <Col flex="auto">
            {selectedPickup?.receiver ? (
              <Typography.Text strong style={{ fontSize: 13, color: "#374151" }}>
                {join(compact([selectedPickup.receiver.fullName, selectedPickup.receiver.phone]), " | ")}
              </Typography.Text>
            ) : (
              <Typography.Text style={{ fontSize: 13, color: "#9CA3AF" }}>
                {t("v2.order.detail.modalChangeShippingMethod.pickupEmpty")}
              </Typography.Text>
            )}
          </Col>
          <Col>
            <EditOutlined style={{ color: "#2563EB", fontSize: 13 }} />
          </Col>
        </Row>
      </div>

      <PickupWarehousePanel
        sellerID={order?.sellerID}
        selectedWarehouseID={selectedPickup?.warehouseID}
        selectedReceiverID={selectedPickup?.receiverID}
        onSelectWarehouse={(warehouse) =>
          setSelectedPickup((prev) => ({ ...prev, warehouseID: warehouse?.id, warehouse }))
        }
        onSelectReceiver={(receiver) => setSelectedPickup((prev) => ({ ...prev, receiverID: receiver?.id, receiver }))}
      />
    </>
  );

  const additionalFieldByShippingType = {
    [SHIPPING_TYPE.BUS]: busConfigFields,
    [SHIPPING_TYPE.DELIVER_3PARTY]: providerField,
    [SHIPPING_TYPE.DELIVER_3PARTY_INSTANT]: providerField,
    [SHIPPING_TYPE.PICK_UP_COUNTER]: pickupField,
    [SHIPPING_TYPE.URBAN_COD]: urbanCODField,
    [SHIPPING_TYPE.URBAN_NOT_COD]: urbanCODField,
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={720}
      centered
      destroyOnClose
      title={
        <Typography.Text strong style={{ fontSize: 16, display: "block", textAlign: "center" }}>
          <Trans
            i18nKey="v2.order.detail.modalChangeShippingMethod.title"
            values={{ orderCode: order?.number || "" }}
            components={{ style: <span style={{ color: "#2246DC", fontWeight: 700 }} /> }}
          />
        </Typography.Text>
      }
      footer={
        <Row justify="center" gutter={12}>
          <Col>
            <Button onClick={onClose} style={{ minWidth: 120 }}>
              {t("v2.order.detail.modalChangeShippingMethod.cancel")}
            </Button>
          </Col>
          <Col>
            <Button type="primary" onClick={handleConfirm} loading={submitting} style={{ minWidth: 120 }}>
              {t("v2.order.detail.modalChangeShippingMethod.confirm")}
            </Button>
          </Col>
        </Row>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={renderRequiredLabel(t("v2.order.detail.modalChangeShippingMethod.shippingTypeLabel"))}
          name="shippingType"
          rules={[{ required: true, message: t("v2.order.detail.modalChangeShippingMethod.blankShippingType") }]}
          required={false}
        >
          <Radio.Group
            onChange={(e) => onChangeShippingType(e.target.value)}
            style={{ display: "flex", flexWrap: "wrap", rowGap: 8 }}
          >
            {shippingTypeOptions?.map((option) => (
              <Radio key={option?.value} value={option?.value} disabled={option?.disabled}>
                {option?.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        {additionalFieldByShippingType[shippingType]}
      </Form>
    </Modal>
  );
};

export default ModalChangeShippingMethod;
